// src/components/Header/Nav/MegaMenu.tsx
"use client"

import * as React from "react"
import clsx from "clsx"
import { createPortal } from "react-dom"
import { ChevronDown } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion" // Added Framer Motion imports
import { usePathname } from "next/navigation"
import { CMSLink } from "@/components/Link"

type LinkField = any
export type MegaMenuBlock = {
  blockType: "megaMenu"
  label: string
  description?: string // <-- added
  columns?: Array<{ heading?: string; items?: Array<{ link: LinkField }> }>
}

// --- portal ---
function usePortalRoot(id = "mm-portal-root") {
  const [el, setEl] = React.useState<HTMLElement | null>(null)
  React.useEffect(() => {
    let node = document.getElementById(id)
    if (!node) {
      node = document.createElement("div")
      node.id = id
      document.body.appendChild(node)
    }
    setEl(node)
  }, [id])
  return el
}
const Portal: React.FC<React.PropsWithChildren<{ rootId?: string }>> = ({ children, rootId }) => {
  const root = usePortalRoot(rootId)
  if (!root) return null
  return createPortal(children, root)
}

// --- header metrics ---
function useHeaderMetrics(anchorId = "site-header") {
  const [bottomY, setBottomY] = React.useState(0)
  const [height, setHeight] = React.useState(0)
  const measure = React.useCallback(() => {
    const el = document.getElementById(anchorId)
    if (!el) return setBottomY(0), setHeight(0)
    const r = el.getBoundingClientRect()
    setBottomY(r.bottom + window.scrollY)
    setHeight(r.height)
  }, [anchorId])
  React.useLayoutEffect(() => {
    measure()
  }, [measure])
  React.useEffect(() => {
    window.addEventListener("resize", measure)
    window.addEventListener("scroll", measure, { passive: true })
    return () => {
      window.removeEventListener("resize", measure)
      window.removeEventListener("scroll", measure)
    }
  }, [measure])
  return { bottomY, height }
}

export const MegaMenu: React.FC<{
  block: MegaMenuBlock
  onToggle?: (isOpen: boolean) => void
}> = ({ block, onToggle }) => {
  const [open, setOpen] = React.useState(false)

  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const panelRef = React.useRef<HTMLDivElement | null>(null)

  const { bottomY: panelTop, height: headerHeight } = useHeaderMetrics("site-header")
  const pathname = usePathname()

  const onToggleClick = () => {
    const newOpen = !open
    setOpen(newOpen)
    onToggle?.(newOpen)
  }

  // close on Esc / outside
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false)
        onToggle?.(false)
      }
    }
    const onDocClick = (e: MouseEvent) => {
      if (!open) return
      const t = e.target as Node
      if (!panelRef.current?.contains(t) && !triggerRef.current?.contains(t)) {
        setOpen(false)
        onToggle?.(false)
      }
    }
    document.addEventListener("keydown", onKey)
    document.addEventListener("mousedown", onDocClick)
    return () => {
      document.removeEventListener("keydown", onKey)
      document.removeEventListener("mousedown", onDocClick)
    }
  }, [open, onToggle])

  React.useEffect(() => {
    setOpen(false)
    onToggle?.(false)
  }, [onToggle, pathname])

  const menuId = React.useId()

  return (
    <div className="relative">
      {/* Trigger: white, pointer, underline */}
      <button
        ref={triggerRef}
        type="button"
        className={clsx(
          "inline-flex items-center gap-1 rounded-lg px-2 py-1",
          "cursor-pointer text-lg font-medium underline-offset-4",
          open ? "underline" : "hover:underline",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={onToggleClick}
      >
        {block.label}
        <ChevronDown className={clsx("size-4 transition-transform", open && "rotate-180")} />
      </button>

      <AnimatePresence>
        {open && (
          <Portal>
            {/* Panel placed just under the header */}
            <div
              ref={panelRef}
              id={menuId}
              role="menu"
              className="absolute inset-x-0 z-[70] text-white"
              style={{ top: panelTop }}
            >
              <motion.div
                initial={{ y: "-100%" }}
                animate={{ y: 0 }}
                exit={{ y: "-100%" }}
                transition={{
                  duration: 0.4,
                  ease: [0.5, 0.0, 0.2, 1],
                }}
                className="origin-top will-change-transform"
              >
                {/* Dark background that ALSO extends up behind the header */}
                <div
                  className="border-t border-white/10 bg-minigreen-7 dark:bg-minigreen-bg-dark shadow-xl"
                  // className="border-t border-white/10 bg-minigreen-7/90 backdrop-blur"
                  style={{ marginTop: -headerHeight, paddingTop: headerHeight }}
                >
                  {/* CONTENT */}
                  <div className="container mx-auto px-6 py-10 pl-14 pt-12">
                    {/* LEFT intro (not clickable) + RIGHT link columns */}
                    <div className="grid gap-12 md:grid-cols-4">
                      {/* LEFT: current tab label + description (visually distinct, not interactive) */}
                      <aside className="md:col-span-1 md:pr-8 md:border-r md:border-white/10" aria-hidden="true">
                        <h3 className="text-lg tracking-wide text-mini-dark-primary font-medium cursor-default select-text">
                          {block.label}
                        </h3>
                        {block.description && (
                          <p className="mt-3 text-sm leading-relaxed text-mini-dark-secondary cursor-default select-text">
                            {block.description}
                          </p>
                        )}
                      </aside>

                      {/* RIGHT: link columns */}
                      <div className="md:col-span-3 md:pl-8 grid gap-10 md:grid-cols-3">
                        {(block.columns || []).map((col, i) => (
                          <div key={i} className="min-w-0">
                            {/* Headings: bigger, NOT bold */}
                            {col.heading && (
                              <div className="mb-3 text-sm uppercase tracking-wider text-mini-dark-secondary font-normal">
                                {col.heading}
                              </div>
                            )}
                            <ul className="space-y-2">
                              {(col.items || []).map((it, j) => (
                                <li key={j}>
                                  {/* Links: larger + bold */}
                                  <CMSLink
                                    {...it.link}
                                    appearance="link"
                                    className="block rounded-lg px-1 py-1.5 text-base font-semibold text-white cursor-pointer hover:underline focus:underline focus:outline-none"
                                  />
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  {/* /CONTENT */}
                </div>
                {/* /dark bar */}
              </motion.div>
            </div>
          </Portal>
        )}
      </AnimatePresence>
    </div>
  )
}
